/**
 * DeactivateUserDialog Component
 *
 * Confirmation dialog for deactivating users.
 * Shows user details and requires confirmation before proceeding.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import AdminButton from '../../../../../components/common/AdminButton';

// Services
import { deactivateUser } from '../services/userService';

// Constants
import { MESSAGES } from '../constants';

/**
 * DeactivateUserDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {function} props.onSuccess - Callback when user is deactivated successfully
 * @param {Object} props.user - User object to deactivate
 * @param {Object} props.currentUser - Currently logged in user (to prevent self-deactivation)
 */
export default function DeactivateUserDialog({ open, onClose, onSuccess, user, currentUser }) {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const isOwnAccount = user && currentUser && user.id === currentUser.id;

  const handleConfirm = async () => {
    if (!user) return;

    // Prevent self-deactivation
    if (isOwnAccount) {
      enqueueSnackbar(MESSAGES.CANNOT_EDIT_SELF, { variant: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      await deactivateUser(user.id);
      enqueueSnackbar(MESSAGES.USER_DEACTIVATED_SUCCESS, { variant: 'success' });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error deactivating user:', error);

      // Handle specific error cases
      if (error.message?.includes('network') || error.message?.includes('Network')) {
        enqueueSnackbar(MESSAGES.NETWORK_ERROR, { variant: 'error' });
      } else if (error.message?.includes('permission') || error.message?.includes('unauthorized')) {
        enqueueSnackbar(MESSAGES.PERMISSION_DENIED, { variant: 'error' });
      } else {
        enqueueSnackbar(MESSAGES.USER_DEACTIVATE_ERROR, { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Deactivate User</DialogTitle>

      <DialogContent>
        {isOwnAccount ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {MESSAGES.CANNOT_EDIT_SELF}
          </Alert>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to deactivate this user?
            </Typography>

            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Name:</strong> {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {user.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Username:</strong> {user.userName}
              </Typography>
              <Typography variant="body2">
                <strong>Group Role:</strong> {user.groupRole}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              This action will set the user's status to DELETED. The user will no longer be able to
              access the system. This action can be reversed by an administrator.
            </Alert>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <AdminButton onClick={onClose} disabled={submitting} variant="outlined">
          Cancel
        </AdminButton>
        <AdminButton
          onClick={handleConfirm}
          variant="contained"
          disabled={isOwnAccount || submitting}
          sx={{
            backgroundColor: '#DC2626',
            '&:hover': {
              backgroundColor: '#B91C1C',
            },
            '&:active': {
              backgroundColor: '#991B1B',
            },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
              Deactivating...
            </>
          ) : (
            'Deactivate User'
          )}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

DeactivateUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  user: PropTypes.object,
  currentUser: PropTypes.object,
};
